/**
 * SKU 単位の予約販売（isBackOrderEnable）に応じて PDP の UI・カート投入を制御する。
 * 商品テンプレート `pre-order` は従来どおり Liquid 側で一元管理し、本スクリプトでは SDK を読まない。
 *
 * 確認用: preorderDebugEnable() → カートボタンクリックで console に出力。
 * または ?preorder_debug=1、または preorderDebugLog() で即時出力。
 *
 * 一括確認（パスワードページ以外ならどのページでも console から可・themeVariables 必須）:
 *   await preorderDebugScanCollection('all')
 *   await preorderDebugScanProductIds([123, 456], { delayMs: 200 })
 * ※ theme.liquid で SQ SDK より後に preorder.js を読み込むこと。
 */
(function () {
  // =====================================================================
  // 定数定義
  // - 2 回以上コード中で参照されるセレクタ・文字列・属性・カート properties
  //   キーをここに集約する。Liquid / main-cart.liquid / カート API と
  //   結びついているため、文字列の値は絶対に変更しない。
  // =====================================================================

  /** DOM セレクタ（2 箇所以上で使用するものだけ集約） */
  const SELECTORS = {
    addToCart: '#AddToCart',
    preOrderFlag: '.js-preorder-cart-flag',
    productForm: 'form[is="product-form"]',
    productVariantsHandle: 'product-variants[handle]',
    loaderButtonText: '.loader-button__text',
    variantIdInput: '[name="id"]',
  };

  /** カート line item の properties キー。ギフトラップ誤検知防止にも参照するので変更不可 */
  const CART_PROPS = {
    preOrder: '__is_preorder',
    giftWrap: '__is_gift_wrap',
  };

  /** theme strings 未設定時のフォールバックボタンラベル */
  const FALLBACK_LABELS = {
    preOrder: '予約商品',
    addToCart: 'カートに追加',
  };

  /** 混在カート防止と cart 検証失敗時のアラート文言（PDP・Quick View 双方で共有） */
  const ALERT_MESSAGES = {
    preOrderBlockedByNormal: '予約商品と通常商品は同時にカートへ入れることができません。',
    normalBlockedByPreOrder: '予約商品と通常商品は同時にカートへ入れることができません。',
    preOrderSameProductDifferentVariant: '同一品番の予約商品でも、カラー・サイズ違いは同時にご注文いただけません。',
    preOrderDifferentProduct: '予約商品は 1 品番のみカートへお入れいただけます。',
    preOrderAlreadyInCart: '予約商品は 1 点のみご注文いただけます。',
    cartValidationFailed: 'カートの確認に失敗しました。しばらくしてからお試しください。',
  };

  /** preorder デバッグの sessionStorage キー */
  const PREORDER_DEBUG_STORAGE_KEY = 'preorder_debug';

  const tv = window.themeVariables;
  const routes = tv && tv.routes;

  // =====================================================================
  // デバッグツール: 予約 SKU 一括確認
  // - 任意のページの console から呼び出し、コレクション単位・ID 配列単位で
  //   isBackOrderEnable が true のバリアントを持つ商品を列挙する。
  // - 通常の PDP/Quick View ロジックとは独立しており、SDK 未読込時は何もしない。
  // =====================================================================

  /**
   * SQ / GraphQL の variant id から Shopify 数値 ID を推定する（デバッグ版）。
   * - 通常版 `normalizeVariantId` と違い GID にマッチしない場合も parseInt で
   *   数値化を試みる。デバッグで id が UUID 的でも「可能な限り数値化して見せる」ため。
   * @param {string | number} id
   * @returns {number | null}
   */
  function preorderDebugNormalizeVariantId(id) {
    if (id == null || id === '') {
      return null;
    }
    if (typeof id === 'number' && !Number.isNaN(id)) {
      return id;
    }
    const idString = String(id);
    const gidMatch = idString.match(/ProductVariant\/(\d+)\s*$/);
    if (gidMatch) {
      return parseInt(gidMatch[1], 10);
    }
    const parsedNumber = parseInt(idString, 10);
    return Number.isNaN(parsedNumber) ? null : parsedNumber;
  }

  /**
   * 複数の Shopify product ID に対して SQ getProduct を順に実行し、
   * isBackOrderEnable が true のバリアントを持つ商品だけを列挙する。
   * @param {number[]} productIds
   * @param {{ delayMs?: number }} [opts]
   * @returns {Promise<Array<{ productId: number, title?: string, preorderVariants: object[] }>>}
   */
  window.preorderDebugScanProductIds = async function (productIds, opts) {
    opts = opts || {};
    const delayMs = opts.delayMs != null ? opts.delayMs : 150;

    // Step 1: SQ SDK インスタンスを取得または生成
    let sdk = window.__SQ_SDK__;
    if (!sdk && typeof window.SQ !== 'undefined' && window.SQ.SDK) {
      sdk = new window.SQ.SDK(window.location.origin);
      window.__SQ_SDK__ = sdk;
    }
    if (!sdk) {
      console.error('[Preorder 一括確認] SQ SDK がありません。sq-shopify-theme-sdk の読み込み後に実行してください。');
      return [];
    }

    // Step 2: 入力 ID を整数化・重複排除
    const uniqueProductIds = [];
    const seenProductIds = Object.create(null);
    (productIds || []).forEach(function (rawId) {
      const parsedProductId = parseInt(String(rawId), 10);
      if (!Number.isNaN(parsedProductId) && !seenProductIds[parsedProductId]) {
        seenProductIds[parsedProductId] = true;
        uniqueProductIds.push(parsedProductId);
      }
    });

    // Step 3: 1 件ずつ SQ getProduct を呼び、予約バリアントがあるものだけ hits に積む
    const hits = [];
    console.group('[Preorder 一括確認] Shopify productId × ' + uniqueProductIds.length + ' 件 → SQ getProduct');
    for (let i = 0; i < uniqueProductIds.length; i++) {
      const productId = uniqueProductIds[i];
      try {
        const product = await sdk.getProduct(String(productId), 250);
        if (!product || !product.variants || !Array.isArray(product.variants.nodes)) {
          console.warn('データなしまたは形式不一致', { productId: productId, raw: product });
        } else {
          const preorderVariants = [];
          product.variants.nodes.forEach(function (variant) {
            if (!variant || !variant.isBackOrderEnable) {
              return;
            }
            const rawSku = variant.inventoryItem && variant.inventoryItem.sku != null ? variant.inventoryItem.sku : '';
            preorderVariants.push({
              sqVariantId: variant.id,
              shopifyVariantIdGuess: preorderDebugNormalizeVariantId(variant.id),
              sku: String(rawSku).trim() || '（空）',
              title: variant.title || '',
            });
          });
          if (preorderVariants.length > 0) {
            const hitRow = { productId: productId, title: product.title, preorderVariants: preorderVariants };
            hits.push(hitRow);
            console.log('予約バリエーションあり', hitRow);
          }
        }
      } catch (err) {
        console.warn('[Preorder 一括確認] getProduct 失敗', {
          productId: productId,
          error: err && err.message ? err.message : err,
        });
      }
      // API スパイク回避のため各呼び出し間にスリープ（最後の 1 件を除く）
      if (delayMs > 0 && i < uniqueProductIds.length - 1) {
        await new Promise(function (resolve) {
          setTimeout(resolve, delayMs);
        });
      }
    }
    console.log('--- 結果サマリ（予約 isBackOrderEnable が1件以上ある商品のみ）---', hits);
    console.info('件数: ' + hits.length + ' / 走査 ' + uniqueProductIds.length);
    console.groupEnd();
    return hits;
  };

  /**
   * コレクションの products.json をページングして ID を集め、preorderDebugScanProductIds に渡す。
   * ストアに「すべて」コレクション（handle: all）があるのが一般的。
   * @param {string} [collectionHandle]
   * @param {{ limit?: number, maxPages?: number, delayMs?: number }} [opts]
   * @returns {Promise<object[]>}
   */
  window.preorderDebugScanCollection = async function (collectionHandle, opts) {
    if (!routes) {
      console.error('[Preorder 一括確認] themeVariables.routes がありません。');
      return [];
    }
    opts = opts || {};
    const handle = collectionHandle || 'all';
    const limit = opts.limit != null ? opts.limit : 250;
    const maxPages = opts.maxPages != null ? opts.maxPages : 50;
    const rootNoSlash = routes.rootUrlWithoutSlash || '';
    const productIds = [];

    // Step 1: products.json をページネーションで走査し ID を集める
    for (let page = 1; page <= maxPages; page++) {
      const url =
        rootNoSlash + '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + limit + '&page=' + page;
      let pageJson;
      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.error('[Preorder 一括確認] products.json 失敗', response.status, url);
          break;
        }
        pageJson = await response.json();
      } catch (fetchErr) {
        console.error('[Preorder 一括確認] fetch エラー', url, fetchErr);
        break;
      }
      if (!pageJson.products || pageJson.products.length === 0) {
        break;
      }
      pageJson.products.forEach(function (productJson) {
        if (productJson && productJson.id != null) {
          productIds.push(productJson.id);
        }
      });
      // 取得件数が limit 未満なら最終ページとみなし打ち切る
      if (pageJson.products.length < limit) {
        break;
      }
    }

    // Step 2: 収集した ID 群を SQ 照会に回す
    console.info(
      '[Preorder 一括確認] コレクション "' +
        handle +
        '" から productId ' +
        productIds.length +
        ' 件を取得。続けて SQ を照会します…',
    );
    return window.preorderDebugScanProductIds(productIds, opts);
  };

  // =====================================================================
  // Quick View: 動的挿入される product-form を MutationObserver で監視
  // - コレクションページ等 pageType !== 'product' でも動作させる。
  // - 商品ごとに独立した SQ マップ（preOrderByShopifyId / preOrderBySku）を
  //   持ち、variant 切替・submit 時に予約判定・ボタン文言切替を行う。
  // =====================================================================

  document.addEventListener('DOMContentLoaded', function () {
    const strings = (window.themeVariables && window.themeVariables.strings) || {};
    const quickViewPreOrderLabel =
      strings.productFormPreorderProduct || strings.productFormPreOrder || FALLBACK_LABELS.preOrder;
    const quickViewAddToCartLabel = strings.productFormAddToCart || FALLBACK_LABELS.addToCart;

    /**
     * Quick View 専用の variant ID 正規化。GraphQL GID を Shopify 数値 ID に変換する。
     * デバッグ版と違い parseInt フォールバックを持たないのは、UUID を誤って数値扱いすると
     * 予約判定マップが壊れて既存バリアントが全て「予約扱い」になるリスクがあるため。
     * @param {string | number} id
     * @returns {number | null}
     */
    function qvNormalizeVariantId(id) {
      if (id == null || id === '') {
        return null;
      }
      if (typeof id === 'number' && !Number.isNaN(id)) {
        return id;
      }
      const idString = String(id);
      const gidMatch = idString.match(/ProductVariant\/(\d+)\s*$/);
      if (gidMatch) {
        return parseInt(gidMatch[1], 10);
      }
      return null;
    }

    /**
     * loader-button 内の表示と data-button-content を同時に書き換える。
     * theme 側が variant 切替時に data-button-content を読み戻すため、両方を揃えないと
     * 直後にラベルが元（「カートに追加」）に戻ってしまう。
     * @param {HTMLElement | null} buttonElement
     * @param {string} labelText
     */
    function qvSetLabel(buttonElement, labelText) {
      if (!buttonElement) {
        return;
      }
      buttonElement.setAttribute('data-button-content', labelText);
      const labelTextElement = buttonElement.querySelector(SELECTORS.loaderButtonText);
      if (labelTextElement) {
        labelTextElement.textContent = labelText;
      } else {
        buttonElement.textContent = labelText;
      }
    }

    /**
     * Quick View 内の 1 フォームを初期化する。
     * form.__preorderInited フラグで二重初期化を防止（MutationObserver から
     * 同一フォームが複数回通知されるケースがあるため）。
     * @param {HTMLFormElement} form
     */
    function initQuickViewForm(form) {
      if (form.__preorderInited) {
        return;
      }
      const quickViewRoot = form.closest('quick-buy-popover, quick-buy-drawer');
      if (!quickViewRoot) {
        return;
      }
      form.__preorderInited = true;

      const productVariantsElement = quickViewRoot.querySelector(SELECTORS.productVariantsHandle);
      const productHandle = productVariantsElement ? productVariantsElement.getAttribute('handle') : null;
      if (!productHandle) {
        return;
      }

      const rootNoSlash = (tv && tv.routes && tv.routes.rootUrlWithoutSlash) || '';
      const productJsonUrl =
        (rootNoSlash === '' ? '' : rootNoSlash) + '/products/' + encodeURIComponent(productHandle) + '.js';

      /** Shopify 数値 variant ID → 予約フラグ */
      /** @type {Record<number, boolean>} */
      const preOrderByShopifyId = {};
      /** SKU → 予約フラグ（SQ 側 id が UUID で Shopify 数値 ID と合わない場合の照合用） */
      /** @type {Record<string, boolean>} */
      const preOrderBySku = {};
      /** /products.js から得た variant メタ（id → { sku }）。variant.sku 欠落時の補完に使う */
      /** @type {Record<number, { sku?: string }>} */
      const shopifyVariantById = {};
      let isQuickViewReady = false;
      let currentQuickViewVariant = null;

      /**
       * 与えられた variant が予約バリアントかを SQ マップで判定する。
       * variant.sku が欠落していた場合は /products.js 由来の sku で補完してから突合する。
       * @param {{ id?: number | string, sku?: string | null } | null} variant
       * @returns {boolean}
       */
      function isPreorder(variant) {
        if (!variant) {
          return false;
        }
        const variantIdNumber = qvNormalizeVariantId(variant.id);
        let variantSku = variant.sku != null ? String(variant.sku).trim() : '';
        if (!variantSku && variantIdNumber != null && shopifyVariantById[variantIdNumber]) {
          variantSku = String(shopifyVariantById[variantIdNumber].sku || '').trim();
        }
        if (variantSku && preOrderBySku[variantSku] === true) {
          return true;
        }
        if (variantIdNumber != null && preOrderByShopifyId[variantIdNumber] === true) {
          return true;
        }
        return false;
      }

      /**
       * Quick View 内の UI（ボタン文言・予約フラグ hidden）を予約 / 通常で切り替える。
       * SQ 取得前は isQuickViewReady が false のまま早期リターンし、判定未確定のまま
       * ラベルが変わって誤案内されるのを防ぐ。
       * @param {object | null} [variant]
       */
      function syncUI(variant) {
        if (!isQuickViewReady) {
          return;
        }
        const targetVariant = variant || currentQuickViewVariant;
        const variantAvailable = !!(targetVariant && targetVariant.available !== false);
        if (!variantAvailable) {
          return;
        }
        const isBackOrder = isPreorder(targetVariant);
        const addToCartButton = quickViewRoot.querySelector('#AddToCart, [data-product-add-to-cart-button]');
        qvSetLabel(addToCartButton, isBackOrder ? quickViewPreOrderLabel : quickViewAddToCartLabel);
        const preOrderFlag = quickViewRoot.querySelector(SELECTORS.preOrderFlag);
        if (preOrderFlag) {
          if (isBackOrder) {
            preOrderFlag.removeAttribute('disabled');
          } else {
            preOrderFlag.setAttribute('disabled', 'disabled');
          }
        }
        // 予約商品注記エリアをバリアント単位で表示制御（QVスコープ限定で取得）
        const deliveryWrapper = quickViewRoot.querySelector('.c_delivery-date--wrapper');
        if (deliveryWrapper) {
          deliveryWrapper.style.display = isBackOrder ? '' : 'none';
        }
      }

      form.addEventListener('variant:changed', function (e) {
        const variant = e.detail && e.detail.variant ? e.detail.variant : null;
        currentQuickViewVariant = variant;
        syncUI(variant);
      });

      form.addEventListener(
        'submit',
        function (e) {
          // バリデーション済み再入は素通しする（fetch 完了後に form を再 submit するため）
          if (form.dataset.preorderValidateDone === '1') {
            delete form.dataset.preorderValidateDone;
            return;
          }

          const variantIdInput = form.querySelector(SELECTORS.variantIdInput);
          const variantId = variantIdInput ? parseInt(variantIdInput.value, 10) : NaN;
          const variantRow = !Number.isNaN(variantId) ? shopifyVariantById[variantId] : undefined;
          const isPreorderLine = isPreorder(
            Number.isNaN(variantId) ? null : { id: variantId, sku: variantRow && variantRow.sku },
          );

          e.preventDefault();
          e.stopImmediatePropagation();

          // カート内の構成を確認して予約×通常の混在をブロックする。
          // ギフトラップ（__is_gift_wrap）は「通常商品」判定から除外する必要がある。
          // これを怠ると、ギフトラップ付き予約商品を追加した後、他の予約商品を追加できない不具合が再発する。
          fetch(((tv && tv.routes && tv.routes.cartUrl) || '/cart').replace(/\/?$/, '') + '.js')
            .then(function (response) {
              return response.json();
            })
            .then(function (cartResponse) {
              const cartItems = cartResponse.items || [];
              // 予約同士の制限（同一品番×別variant・別品番）判定のため、line 単位で集約する。
              // ギフトラップは既存踏襲で集約前に除外しておく。
              const cartLines = cartItems
                .filter(function (item) {
                  return !(item.properties && item.properties[CART_PROPS.giftWrap]);
                })
                .map(function (item) {
                  return {
                    productId: item.product_id,
                    variantId: item.variant_id,
                    isPreorder: !!(item.properties && item.properties[CART_PROPS.preOrder]),
                  };
                });
              const cartHasNormal = cartLines.some(function (l) {
                return !l.isPreorder;
              });
              const preorderLines = cartLines.filter(function (l) {
                return l.isPreorder;
              });
              const cartHasPreorder = preorderLines.length > 0;

              if (isPreorderLine && cartHasNormal) {
                AlphaErrorModal.show({ title: ALERT_MESSAGES.preOrderBlockedByNormal });
                return;
              }
              if (!isPreorderLine && cartHasPreorder) {
                AlphaErrorModal.show({ title: ALERT_MESSAGES.normalBlockedByPreOrder });
                return;
              }

              // 予約同士の制限（厳格仕様: 予約はカート内に高々 1 点まで）
              // 同一 variant の数量追加・同一品番の別variant・別品番、いずれも無条件ブロック
              if (isPreorderLine && cartHasPreorder) {
                AlphaErrorModal.show({ title: ALERT_MESSAGES.preOrderAlreadyInCart });
                return;
              }

              form.dataset.preorderValidateDone = '1';
              if (typeof form.requestSubmit === 'function') {
                form.requestSubmit();
              } else {
                form.submit();
              }
            })
            .catch(function (err) {
              console.error('[Preorder QV] cart validation error:', err);
              AlphaErrorModal.show({ title: ALERT_MESSAGES.cartValidationFailed });
            });
        },
        true,
      );

      // Shopify /products.js で variant メタを取得 → SQ getProduct で予約フラグ表を構築
      (async function () {
        let productId = null;
        try {
          const response = await fetch(productJsonUrl);
          if (!response.ok) {
            throw new Error('qv /products.js ' + response.status);
          }
          const productJson = await response.json();
          productId = productJson.id;
          (productJson.variants || []).forEach(function (variantRow) {
            if (variantRow && variantRow.id != null) {
              shopifyVariantById[variantRow.id] = { sku: variantRow.sku };
            }
          });
        } catch (err) {
          console.warn('[Preorder QV] /products.js 失敗', err);
          return;
        }

        const sdk =
          window.__SQ_SDK__ || (window.SQ && window.SQ.SDK ? new window.SQ.SDK(window.location.origin) : null);
        if (!sdk) {
          console.warn('[Preorder QV] SQ SDK 未読み込みのため判定不可');
          return;
        }

        try {
          const sqProduct = await sdk.getProduct(String(productId), 250);
          if (sqProduct && sqProduct.variants && Array.isArray(sqProduct.variants.nodes)) {
            sqProduct.variants.nodes.forEach(function (variant) {
              const isBackOrder = !!variant.isBackOrderEnable;
              const normalizedId = qvNormalizeVariantId(variant.id);
              if (normalizedId != null) {
                preOrderByShopifyId[normalizedId] = isBackOrder;
              }
              const rawSku =
                variant.inventoryItem && variant.inventoryItem.sku != null ? variant.inventoryItem.sku : '';
              const sku = String(rawSku).trim();
              if (sku) {
                preOrderBySku[sku] = isBackOrder;
              }
            });
          }
        } catch (err) {
          console.warn('[Preorder QV] SQ 取得失敗', err);
          return;
        }

        // SQ マップが揃ってから UI 同期を解禁する（ここより前の syncUI 呼び出しは早期リターン済み）
        isQuickViewReady = true;

        if (currentQuickViewVariant) {
          syncUI(currentQuickViewVariant);
        } else {
          const variantIdInput = form.querySelector(SELECTORS.variantIdInput);
          const variantId = variantIdInput ? parseInt(variantIdInput.value, 10) : NaN;
          if (!Number.isNaN(variantId)) {
            const variantRow = shopifyVariantById[variantId];
            syncUI({ id: variantId, sku: variantRow && variantRow.sku, available: true });
          }
        }
      })();
    }

    /**
     * 指定ルート内の product-form を初期化対象として走査する。
     * @param {Element | null} root
     */
    function qvScanRoot(root) {
      if (!root) {
        return;
      }
      if (root.matches && root.matches(SELECTORS.productForm)) {
        initQuickViewForm(root);
      }
      if (root.querySelectorAll) {
        root.querySelectorAll(SELECTORS.productForm).forEach(initQuickViewForm);
      }
    }

    // body 全体を監視。quick view の drawer/popover は後からスクリプトで挿入されるため subtree: true 必須
    const quickViewObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (addedNode) {
          if (addedNode.nodeType !== 1) {
            return;
          }
          qvScanRoot(addedNode);
        });
      });
    });
    quickViewObserver.observe(document.body, { childList: true, subtree: true });

    // 初期ロード時点で既にある product-form も走査
    qvScanRoot(document.body);
  });

  // =====================================================================
  // Cart: 予約商品の数量 UI を非表示
  // - 予約商品はカート上での数量変更を禁止（1 注文 = 1 セットの運用前提）。
  // - `line-item-quantity[data-product-id]` が付与されていることを main-cart.liquid に依存。
  // =====================================================================

  /**
   * カートページで予約商品の数量変更 UI を非表示にする。
   * main-cart.liquid 側で `<line-item-quantity data-product-id="..." data-variant-id="...">` を付与している前提。
   */
  document.addEventListener('DOMContentLoaded', function () {
    if (!document.querySelector('line-item-quantity[data-product-id]')) {
      return;
    }

    (async function handleCartQuantityVisibility() {
      const sdk = window.__SQ_SDK__ || (window.SQ && window.SQ.SDK ? new window.SQ.SDK(window.location.origin) : null);
      if (!sdk) {
        console.warn('[Preorder Cart] SQ SDK 未読み込みのため数量UI制御不可');
        return;
      }

      const lineItemContainers = document.querySelectorAll('line-item-quantity[data-product-id]');
      /** 同一商品への API 重複呼び出しを避けるキャッシュ */
      const checkedProductIds = Object.create(null);
      for (const container of lineItemContainers) {
        const productId = container.getAttribute('data-product-id');
        if (!productId) {
          continue;
        }
        try {
          if (checkedProductIds[productId] === undefined) {
            // 軽量 API（getHasBackOrderProductVariants）で「予約バリアントを持つか」だけ確認
            checkedProductIds[productId] = !!(await sdk.getHasBackOrderProductVariants(productId));
          }
          if (checkedProductIds[productId]) {
            // .quantity-selector だけを隠す。line-item-quantity 全体を非表示にすると
            // 削除ボタンも一緒に消えてしまう（過去に発生した不具合）のでコンテナは残す
            const quantitySelector = container.querySelector('.quantity-selector');
            if (quantitySelector) {
              quantitySelector.style.display = 'none';
            } else {
              container.style.display = 'none';
            }
          }
        } catch (err) {
          console.warn('[Preorder Cart] 数量UI制御失敗', err);
        }
      }
    })();
  });

  // =====================================================================
  // Collection: 商品一覧カードに PRE-ORDER バッジを付与
  // - pageType に依存せず、ページ内に `product-item` カードがある画面で動作。
  //   対象は product-item.liquid を render する 9 セクション（TOP、コレクション、
  //   検索、関連商品、最近見た商品、カートレコメンド 等）。PDP 本体の
  //   `product-meta__label-list` は別ロジック `updatePreorderUI` が担当するため対象外。
  // - 軽量 API `getHasBackOrderProductVariants` を商品ID単位で呼び、
  //   結果をセッション内 `window.__PREORDER_COLLECTION_CACHE__` にキャッシュする
  //   （featured/recently-viewed/関連商品で同一商品が複数回出るケースの重複呼び出し抑止）。
  // - use_ajax ページネーション・無限スクロールで後から挿入されるカードは
  //   MutationObserver で検知して同じ処理を適用する。
  // =====================================================================

  document.addEventListener('DOMContentLoaded', function () {
    // `<product-item>` カスタム要素タグと `.product-item` クラスの両方に対応。
    // 一部の動的描画経路でタグは付くが class が付かない（あるいはその逆の）ケースを拾う。
    const CARD_SELECTOR = 'product-item, .product-item';
    const LABEL_LIST_SELECTOR = '.product-item__label-list';
    const PREORDER_BADGE_CLASS = 'js-preorder-badge';

    const preorderCache = (window.__PREORDER_COLLECTION_CACHE__ =
      window.__PREORDER_COLLECTION_CACHE__ || Object.create(null));
    const processedCards = new WeakSet();

    /**
     * `window.__SQ_SDK__` があればそれを返し、無ければ UMD SDK から生成して保存。
     * PDP 未ログイン時は theme.liquid 側で既に生成済みだが、TOP/コレクション等では
     * 生成されないため c_preorder.js 側の fallback に揃える。
     * @returns {object | null}
     */
    function getOrCreateSdk() {
      if (window.__SQ_SDK__) {
        return window.__SQ_SDK__;
      }
      if (typeof window.SQ === 'undefined' || !window.SQ.SDK) {
        return null;
      }
      window.__SQ_SDK__ = new window.SQ.SDK(window.location.origin);
      return window.__SQ_SDK__;
    }

    /**
     * PDP の updatePreorderUI が生成するバッジと同一の DOM を返す。
     * CSS（`label--pre-order`）は PDP と共通。
     * @returns {HTMLSpanElement}
     */
    function createPreorderBadgeElement() {
      const badge = document.createElement('span');
      badge.className = 'label label--custom c_product-label label--pre-order ' + PREORDER_BADGE_CLASS;
      badge.textContent = 'PRE-ORDER';
      return badge;
    }

    /**
     * product-item.liquid 先頭で出力される `<script id="product-json--{id}">` から
     * 商品ID（Shopify numeric ID）を抽出する。data 属性を追加せずに済ませるための選択。
     * @param {Element} card
     * @returns {number | null}
     */
    function extractProductIdFromCard(card) {
      const jsonScript = card.querySelector('[id^="product-json--"]');
      if (!jsonScript) {
        return null;
      }
      const idMatch = jsonScript.id.match(/^product-json--(\d+)$/);
      if (!idMatch) {
        return null;
      }
      const productId = parseInt(idMatch[1], 10);
      return Number.isNaN(productId) ? null : productId;
    }

    /**
     * 1 カードを処理してバッジを付与する（必要なら）。
     * - ラベルリスト非表示（reduced_content モード）ならスキップ
     * - 既にバッジがあればスキップ（Liquid 側 / 再実行 / 非同期競合すべてに対応）
     * - キャッシュヒットなら API コールを省く
     * @param {Element} card
     * @param {object} sdk
     */
    async function processCard(card, sdk) {
      if (processedCards.has(card)) {
        return;
      }
      processedCards.add(card);

      const labelList = card.querySelector(LABEL_LIST_SELECTOR);
      if (!labelList) {
        return;
      }
      if (labelList.querySelector('.' + PREORDER_BADGE_CLASS)) {
        return;
      }

      const productId = extractProductIdFromCard(card);
      if (productId == null) {
        return;
      }

      let hasBackOrder;
      if (preorderCache[productId] !== undefined) {
        hasBackOrder = preorderCache[productId];
      } else {
        try {
          hasBackOrder = !!(await sdk.getHasBackOrderProductVariants(String(productId)));
        } catch (err) {
          console.warn('[Preorder Collection] getHasBackOrderProductVariants 失敗', productId, err);
          return;
        }
        preorderCache[productId] = hasBackOrder;
      }

      if (!hasBackOrder) {
        return;
      }
      // 非同期完了までに他経路（再スキャン等）でバッジが付いた場合の二重挿入防止
      if (labelList.querySelector('.' + PREORDER_BADGE_CLASS)) {
        return;
      }
      labelList.appendChild(createPreorderBadgeElement());
    }

    /**
     * 指定ルート（初期 document.body や Observer が検知した新規ノード）配下の
     * `.product-item` を列挙して processCard に流す。
     * @param {Element | null} root
     * @param {object} sdk
     */
    function scanRoot(root, sdk) {
      if (!root) {
        return;
      }
      const cards = [];
      if (root.matches && root.matches(CARD_SELECTOR)) {
        cards.push(root);
      }
      if (root.querySelectorAll) {
        root.querySelectorAll(CARD_SELECTOR).forEach(function (card) {
          cards.push(card);
        });
      }
      cards.forEach(function (card) {
        processCard(card, sdk);
      });
    }

    const sdk = getOrCreateSdk();
    if (!sdk) {
      console.warn('[Preorder Collection] SQ SDK 未読み込みのため一覧バッジを描画できません');
      return;
    }

    // 初期ロード時点で既に存在するカード（TOP / コレクション / 検索結果等）を処理
    const initialCards = document.querySelectorAll(CARD_SELECTOR);
    initialCards.forEach(function (card) {
      processCard(card, sdk);
    });

    // main-collection.liquid の use_ajax: true による Ajax 差し替え・
    // recently-viewed-products の JS 描画・無限スクロール等に対応
    const collectionObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (addedNode) {
          if (addedNode.nodeType !== 1) {
            return;
          }
          scanRoot(addedNode, sdk);
        });
      });
    });
    collectionObserver.observe(document.body, { childList: true, subtree: true });

    // Recently viewed（<recently-viewed-products>）は localStorage 読み込み →
    // AJAX fetch → DOM 挿入という遅延描画パターンを取る。
    // MutationObserver が親要素追加を検知しても、その瞬間は子カードが
    // まだ構築されていないケースがあり、初回スキャン・addedNodes スキャン
    // だけでは取りこぼす。保険として 1 秒後・3 秒後に全体を再スキャンする。
    // processedCards / preorderCache / 既存バッジチェックにより
    // 二重処理・再 API 呼び出しは発生しない。
    setTimeout(function () {
      scanRoot(document.body, sdk);
    }, 1000);
    setTimeout(function () {
      scanRoot(document.body, sdk);
    }, 3000);
  });

  // =====================================================================
  // PDP: 予約バリアント判定・UI 更新・混在カート防止（本体）
  // - pageType === 'product' のときだけ動作させる。
  // - `__PRODUCT_PRE_ORDER_TEMPLATE__` が true なら SDK を読まずテンプレート前提で確定扱い。
  // - それ以外は SQ SDK で予約マップを構築し、sdkReady が立ってから UI を反映する。
  // =====================================================================

  const pageType = tv && tv.settings ? tv.settings.pageType : null;
  if (pageType !== 'product') {
    return;
  }

  document.addEventListener('DOMContentLoaded', function () {
    const isPreOrderTemplate = window.__PRODUCT_PRE_ORDER_TEMPLATE__ === true;
    const productId = window.__PRODUCT_ID__;
    const sdk = window.__SQ_SDK__;
    const strings = (window.themeVariables && window.themeVariables.strings) || {};

    /** Shopify バリアント ID（数値）→ SQ の isBackOrderEnable */
    /** @type {Record<number, boolean>} */
    const variantBackOrderByShopifyId = {};
    /** SKU（trim 済み）→ SQ の isBackOrderEnable（SQ 側 ID が Shopify と異なる場合の照合用） */
    /** @type {Record<string, boolean>} */
    const variantBackOrderBySku = {};
    /** `/products/*.js` から取得したバリアント（id → 行） */
    /** @type {Record<number, { sku?: string }>} */
    const shopifyVariantById = {};
    let currentVariant = null;
    // sdkReady は SQ getProduct 成功時のみ true にする。null が返ったり例外が出た場合は
    // false のままにして UI 書き換えを抑止する（過去に false 予約扱いで「予約商品」と
    // 誤表示した経緯がある）。
    let sdkReady = false;
    /** @type {boolean[]} */
    let prevDisabledStates = [];

    const preorderLabel = strings.productFormPreorderProduct || strings.productFormPreOrder || FALLBACK_LABELS.preOrder;
    const addToCartLabel = strings.productFormAddToCart || FALLBACK_LABELS.addToCart;

    const addToCartButtons = () =>
      /** @type {HTMLButtonElement[]} */ (
        [document.querySelector(SELECTORS.addToCart), document.querySelector('#StickyAddToCart')].filter(Boolean)
      );

    const preorderFlagInput = () => document.querySelector(SELECTORS.preOrderFlag);

    /**
     * GraphQL の GID と数値 ID の両方に対応する。
     * parseInt フォールバックを持たないのは、UUID を誤って数値扱いすると全バリアントが
     * 予約扱いになる事故を防ぐため（デバッグ版とはあえて挙動が異なる）。
     * @param {string | number} id
     * @returns {number | null}
     */
    function normalizeVariantId(id) {
      if (id == null || id === '') {
        return null;
      }
      if (typeof id === 'number' && !Number.isNaN(id)) {
        return id;
      }
      const idString = String(id);
      const gidMatch = idString.match(/ProductVariant\/(\d+)\s*$/);
      if (gidMatch) {
        return parseInt(gidMatch[1], 10);
      }
      return null;
    }

    /**
     * テーマが渡す variant（Shopify `/products/*.js` 形）に対して SQ の予約フラグを判定する。
     * `pre-order` テンプレート商品なら問答無用で true を返す（Liquid 側で確定済）。
     * @param {{ id?: number | string, sku?: string | null } | null} variant
     * @returns {boolean}
     */
    function variantIsPreorder(variant) {
      if (isPreOrderTemplate) {
        return true;
      }
      if (!variant) {
        return false;
      }
      const variantIdNumber = normalizeVariantId(variant.id);
      let variantSku = variant.sku != null ? String(variant.sku).trim() : '';
      if (!variantSku && variantIdNumber != null && shopifyVariantById[variantIdNumber]) {
        const row = shopifyVariantById[variantIdNumber];
        variantSku = row.sku != null ? String(row.sku).trim() : '';
      }
      if (variantSku && variantBackOrderBySku[variantSku] === true) {
        return true;
      }
      if (variantIdNumber != null && variantBackOrderByShopifyId[variantIdNumber] === true) {
        return true;
      }
      return false;
    }

    /**
     * loader-button 内の表示と theme の variant 同期用 data を揃える。
     * @param {HTMLButtonElement | null} buttonElement
     * @param {string} labelText
     */
    function setAddToCartButtonLabel(buttonElement, labelText) {
      if (!buttonElement) {
        return;
      }
      buttonElement.setAttribute('data-button-content', labelText);
      const labelTextElement = buttonElement.querySelector(SELECTORS.loaderButtonText);
      if (labelTextElement) {
        labelTextElement.textContent = labelText;
      } else {
        buttonElement.textContent = labelText;
      }
    }

    /**
     * 予約状態に応じて PDP 全体の UI を切り替える。
     *
     * - ボタン文言を「予約商品 / カートに追加」で切替
     * - 予約フラグ hidden input を送信対象に含めるか切替
     * - Shopify Pay ボタン（即時決済）は予約時には非表示
     * - ラベルリストへ PRE-ORDER バッジを挿入／除去
     * - 配送日ラッパーへ「こちらの商品は予約商品です」注記を挿入／除去
     *
     * @param {boolean} isBackOrder
     * @param {{ id: number, available?: boolean } | null} [variant]
     */
    function updatePreorderUI(isBackOrder, variant) {
      const targetVariant = variant || currentVariant;
      const variantAvailable = !!(targetVariant && targetVariant.available);
      const showPreorderButton = variantAvailable && isBackOrder;
      const showNormalAdd = variantAvailable && !isBackOrder;

      addToCartButtons().forEach(function (buttonElement) {
        if (!variantAvailable) {
          return;
        }
        if (showPreorderButton) {
          setAddToCartButtonLabel(buttonElement, preorderLabel);
        } else if (showNormalAdd) {
          setAddToCartButtonLabel(buttonElement, addToCartLabel);
        }
      });

      const preorderFlag = preorderFlagInput();
      if (preorderFlag) {
        if (isPreOrderTemplate) {
          preorderFlag.removeAttribute('disabled');
        } else if (isBackOrder) {
          preorderFlag.removeAttribute('disabled');
        } else {
          preorderFlag.setAttribute('disabled', 'disabled');
        }
      }

      toggleOrderRestrictions(isBackOrder);

      document.querySelectorAll('product-payment-container .shopify-payment-button').forEach(function (paymentButton) {
        paymentButton.style.display = !variantAvailable || isBackOrder ? 'none' : '';
      });

      // PRE-ORDER バッジは custom-product-labels 側のラベルリスト（data-product-label-list 属性がない方）に入れる。
      // data-product-label-list 付きは SALE/SOLD OUT 用なので誤って混ぜない。
      const labelList = document.querySelector('.product-meta__label-list:not([data-product-label-list])');
      if (labelList) {
        const existingBadge = labelList.querySelector('.js-preorder-badge');
        if (existingBadge) {
          existingBadge.remove();
        }
        if (isBackOrder) {
          const preorderBadge = document.createElement('span');
          preorderBadge.className = 'label label--custom c_product-label label--pre-order js-preorder-badge';
          preorderBadge.textContent = 'PRE-ORDER';
          labelList.appendChild(preorderBadge);
        }
      }

      // 予約商品注記エリア全体をバリアント単位で表示制御する
      const deliveryWrapper = document.querySelector('.c_delivery-date--wrapper');
      if (deliveryWrapper) {
        deliveryWrapper.style.display = isBackOrder ? '' : 'none';
      }
    }

    /**
     * 予約 SKU 選択時に商品ページ上のオプションを隠す（要素が無ければ何もしない）。
     * @param {boolean} isBackOrder
     */
    function toggleOrderRestrictions(isBackOrder) {
      const deliveryDate = document.querySelector('.js-delivery-date');
      if (deliveryDate) {
        deliveryDate.hidden = isBackOrder;
      }
      const giftWrap = document.querySelector('.js-gift-wrapping');
      if (giftWrap) {
        giftWrap.hidden = isBackOrder;
      }
    }

    /**
     * SDK 取得中のみボタンを無効化し、解除時は元の販売可否に戻す。
     * @param {boolean} loading
     */
    function setButtonsLoading(loading) {
      const buttons = addToCartButtons();
      buttons.forEach(function (buttonElement, index) {
        if (!buttonElement) {
          return;
        }
        if (loading) {
          prevDisabledStates[index] = buttonElement.disabled;
          buttonElement.setAttribute('disabled', 'disabled');
          buttonElement.setAttribute('aria-busy', 'true');
        } else {
          buttonElement.removeAttribute('aria-busy');
          // 元々 enabled だった場合のみ解除。元々 disabled（在庫切れ等）だった場合はそのまま維持
          if (prevDisabledStates[index] === false) {
            buttonElement.removeAttribute('disabled');
          }
        }
      });
    }

    /**
     * 渡された variant で UI を同期する。SDK 未取得かつテンプレート予約でもなければ
     * 判定確定前なので UI 更新を抑止する。
     * @param {{ id: number, available?: boolean } | null} variant
     */
    function syncFromVariant(variant) {
      currentVariant = variant;
      const isBackOrder = variantIsPreorder(variant);
      if (!sdkReady && !isPreOrderTemplate) {
        return;
      }
      updatePreorderUI(isBackOrder, variant);
    }

    function attachVariantListener(form) {
      if (!form) {
        return;
      }
      form.addEventListener('variant:changed', function (e) {
        const variant = e.detail && e.detail.variant ? e.detail.variant : null;
        syncFromVariant(variant);
      });
    }

    const mainForm =
      document.querySelector(SELECTORS.productForm) || document.querySelector(SELECTORS.addToCart)?.form || null;
    attachVariantListener(mainForm);

    /**
     * マスター variant ID とボタン状態から現在バリアントを推定する。
     */
    function readVariantFromDom() {
      const variantIdInput = mainForm && mainForm.querySelector(SELECTORS.variantIdInput);
      const variantId = variantIdInput ? parseInt(variantIdInput.value, 10) : NaN;
      const addToCartButton = addToCartButtons()[0];
      const isAvailable =
        !!addToCartButton && !addToCartButton.disabled && !addToCartButton.classList.contains('button--ternary');
      if (Number.isNaN(variantId)) {
        return null;
      }
      const variantRow = shopifyVariantById[variantId];
      return {
        id: variantId,
        available: isAvailable,
        sku: variantRow && variantRow.sku,
      };
    }

    if (isPreOrderTemplate) {
      // pre-order テンプレート商品: SDK を読まず Liquid 側の設定を真とみなす
      sdkReady = true;
      window.__PREORDER_SDK_READY__ = true;
      currentVariant = readVariantFromDom();
      syncFromVariant(currentVariant);
    } else {
      if (productId == null) {
        return;
      }

      let sdkInstance = window.__SQ_SDK__;
      if (!sdkInstance && typeof window.SQ !== 'undefined' && window.SQ.SDK) {
        sdkInstance = new window.SQ.SDK(window.location.origin);
        window.__SQ_SDK__ = sdkInstance;
      }
      if (!sdkInstance) {
        console.warn(
          'Preorder: SQ SDK が未読み込みのため isBackOrderEnable を取得できません（@builtbystack/sq-shopify-theme-sdk の読み込みを確認してください）。',
        );
        sdkReady = false;
        window.__PREORDER_SDK_READY__ = false;
      } else {
        const productVariantsElement = document.querySelector(SELECTORS.productVariantsHandle);
        const productHandle = productVariantsElement ? productVariantsElement.getAttribute('handle') : null;
        const rootNoSlash =
          (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrlWithoutSlash) ||
          '';
        const shopifyJsonUrl = productHandle
          ? (rootNoSlash === '' ? '' : rootNoSlash) + '/products/' + encodeURIComponent(productHandle) + '.js'
          : null;

        console.info('[Preorder] init productId=' + productId + ' handle=' + productHandle);

        // Step 1: Shopify /products.js から variant 行（sku 補完用）を取得（非同期・失敗しても続行）
        if (shopifyJsonUrl) {
          fetch(shopifyJsonUrl)
            .then(function (response) {
              if (!response.ok) {
                throw new Error('Shopify product JSON ' + response.status);
              }
              return response.json();
            })
            .then(function (shopifyProduct) {
              if (shopifyProduct && Array.isArray(shopifyProduct.variants)) {
                shopifyProduct.variants.forEach(function (variantRow) {
                  if (variantRow && variantRow.id != null) {
                    shopifyVariantById[variantRow.id] = { sku: variantRow.sku };
                  }
                });
                // /products.js の結果を待ってから SQ マップで再同期（sku が後から埋まるため）
                if (sdkReady && currentVariant) {
                  syncFromVariant(currentVariant);
                }
              }
            })
            .catch(function (err) {
              console.warn('Preorder: Shopify /products.js 取得失敗:', err);
            });
        }

        setButtonsLoading(true);

        // Step 2: SQ getProduct で予約マップを構築する。
        //   - first: 250 を渡してページングなし取得
        //   - 成功時にのみ sdkReady = true（null 返しや例外時は false 維持して UI 誤発火を防ぐ）
        sdkInstance
          .getProduct(String(productId), 250)
          .then(function (product) {
            if (!product) {
              console.warn(
                'Preorder: SQ getProduct が null を返しました（productShopifyLegacyResourceID が SQ 側に無い可能性）。 productId=',
                productId,
              );
              throw new Error('getProduct returned null');
            }

            const variantNodes = product.variants && product.variants.nodes;
            if (!Array.isArray(variantNodes)) {
              throw new Error('Unexpected getProduct shape (variants.nodes)');
            }

            let backOrderTrueCount = 0;
            variantNodes.forEach(function (variant) {
              const isBackOrder = !!variant.isBackOrderEnable;
              if (isBackOrder) {
                backOrderTrueCount += 1;
              }
              const normalizedId = normalizeVariantId(variant.id);
              if (normalizedId != null) {
                variantBackOrderByShopifyId[normalizedId] = isBackOrder;
              }
              const rawSku =
                variant.inventoryItem && variant.inventoryItem.sku != null ? variant.inventoryItem.sku : '';
              const sku = String(rawSku).trim();
              if (sku) {
                variantBackOrderBySku[sku] = isBackOrder;
              }
            });

            sdkReady = true;
            window.__PREORDER_SDK_READY__ = true;
            console.info(
              '[Preorder] SQ 取得完了 nodes=' + variantNodes.length + ' backOrderTrue=' + backOrderTrueCount,
            );

            if (currentVariant) {
              syncFromVariant(currentVariant);
            }

            if (variantNodes.length === 0) {
              console.warn(
                'Preorder: SQ getProduct の variants が0件です。第2引数 first を指定済みか、SQ データを確認してください。',
              );
            }
            if (backOrderTrueCount === 0 && variantNodes.length > 0) {
              console.info(
                'Preorder: isBackOrderEnable が true のバリアントがありません（SQ 管理画面・商品同期を確認してください）。',
              );
            }
          })
          .catch(function (err) {
            console.error('SQ getProduct error:', err);
            sdkReady = false;
            window.__PREORDER_SDK_READY__ = false;
          })
          .finally(function () {
            setButtonsLoading(false);
            currentVariant = readVariantFromDom();
            if (sdkReady) {
              syncFromVariant(currentVariant);
            }
          });
      }
    }

    if (!mainForm) {
      return;
    }

    // --- 予約確認モード（debug） -----------------------------------------
    // ?preorder_debug=1 をクエリに付けて PDP を開くと sessionStorage にフラグを立て、
    // 以降カートボタンを押すたびに選択中バリアントの予約判定根拠を console.group で出す。
    try {
      const preorderDebugParam = new URLSearchParams(window.location.search).get('preorder_debug');
      if (preorderDebugParam === '1') {
        sessionStorage.setItem(PREORDER_DEBUG_STORAGE_KEY, '1');
      }
    } catch (errPreorderParam) {
      /* noop */
    }

    /**
     * 確認モードが有効か（sessionStorage または window.__PREORDER_DEBUG__）。
     * @returns {boolean}
     */
    function preorderDebugEnabled() {
      try {
        return sessionStorage.getItem(PREORDER_DEBUG_STORAGE_KEY) === '1' || window.__PREORDER_DEBUG__ === true;
      } catch (e) {
        return window.__PREORDER_DEBUG__ === true;
      }
    }

    /**
     * 現在のバリアントが SQ 上で予約扱いかを console にまとめて出す。
     * @param {string} sourceLabel
     */
    function logPreorderVerification(sourceLabel) {
      const fromEvent = currentVariant;
      const fromDom = readVariantFromDom();
      const variant = fromEvent && fromEvent.id != null ? fromEvent : fromDom;
      const variantIdNumber = variant ? normalizeVariantId(variant.id) : null;
      let variantSku = variant && variant.sku != null ? String(variant.sku).trim() : '';
      if (!variantSku && variantIdNumber != null && shopifyVariantById[variantIdNumber]) {
        variantSku = String(shopifyVariantById[variantIdNumber].sku || '').trim();
      }

      const byId = variantIdNumber != null ? variantBackOrderByShopifyId[variantIdNumber] : undefined;
      const bySku = variantSku ? variantBackOrderBySku[variantSku] : undefined;
      const isPo = variantIsPreorder(variant);
      const preorderFlag = preorderFlagInput();
      const flagDisabled = preorderFlag ? preorderFlag.hasAttribute('disabled') : null;

      console.group('[Preorder 確認] ' + sourceLabel);
      console.log('liquid pre-order テンプレート商品', isPreOrderTemplate);
      console.log('Shopify productId', productId);
      console.log('getProduct 成功（SQ マップ利用可）', sdkReady);
      console.log('window.__PREORDER_SDK_READY__', window.__PREORDER_SDK_READY__);
      console.log('選択中バリアント', {
        id: variantIdNumber,
        sku: variantSku || '（空）',
        available: variant ? variant.available : undefined,
      });
      console.log('SQ isBackOrderEnable（Shopify variant id 照合）', byId);
      console.log('SQ isBackOrderEnable（SKU 照合）', bySku);
      console.log('→ 予約バリエーションとして扱う（ボタン文言・カート属性の根拠）', isPo);
      console.log('properties[__is_preorder] hidden が disabled（送られない）', flagDisabled);
      if (preorderDebugEnabled()) {
        console.log('SQ マップ variantBackOrderByShopifyId', variantBackOrderByShopifyId);
        console.log('SQ マップ variantBackOrderBySku', variantBackOrderBySku);
      }
      console.groupEnd();
    }

    /**
     * カートボタンクリック時に console へ出す確認モードを ON。
     */
    window.preorderDebugEnable = function () {
      try {
        sessionStorage.setItem(PREORDER_DEBUG_STORAGE_KEY, '1');
      } catch (e) {
        /* noop */
      }
      window.__PREORDER_DEBUG__ = true;
      console.info('[Preorder] デバッグ ON。「カートに追加」をクリックするたびに判定を表示します。');
    };

    /**
     * 確認モード OFF。
     */
    window.preorderDebugDisable = function () {
      try {
        sessionStorage.removeItem(PREORDER_DEBUG_STORAGE_KEY);
      } catch (e2) {
        /* noop */
      }
      window.__PREORDER_DEBUG__ = false;
      console.info('[Preorder] デバッグ OFF');
    };

    /**
     * いま選択中のバリアントだけ即座にログ出力（クリック不要）。
     */
    window.preorderDebugLog = function () {
      logPreorderVerification('preorderDebugLog()');
    };

    addToCartButtons().forEach(function (buttonElement) {
      buttonElement.addEventListener(
        'click',
        function () {
          if (preorderDebugEnabled()) {
            logPreorderVerification('カートボタンクリック');
          }
        },
        true,
      );
    });

    if (preorderDebugEnabled()) {
      console.info(
        '[Preorder] デバッグ中です。?preorder_debug=1 または preorderDebugEnable() で有効化済み。OFF は preorderDebugDisable()。',
      );
    }

    // --- 混在カート防止（PDP 側 submit フック） -----------------------------
    mainForm.addEventListener(
      'submit',
      function (e) {
        if (mainForm.dataset.preorderValidateDone === '1') {
          delete mainForm.dataset.preorderValidateDone;
          return;
        }

        const variantIdInput = mainForm.querySelector(SELECTORS.variantIdInput);
        const variantId = variantIdInput ? parseInt(variantIdInput.value, 10) : NaN;
        const variantRow = !Number.isNaN(variantId) ? shopifyVariantById[variantId] : undefined;
        const isPreorderLine = variantIsPreorder(
          Number.isNaN(variantId)
            ? null
            : {
                id: variantId,
                sku: variantRow && variantRow.sku,
              },
        );

        e.preventDefault();
        e.stopImmediatePropagation();

        // Quick View 側と同じロジック。ギフトラップ（__is_gift_wrap）は「通常商品」判定に含めない
        fetch((window.themeVariables.routes.cartUrl || '/cart').replace(/\/?$/, '') + '.js')
          .then(function (response) {
            return response.json();
          })
          .then(function (cartResponse) {
            const cartItems = cartResponse.items || [];
            // 予約同士の制限（同一品番×別variant・別品番）判定のため、line 単位で集約する。
            // ギフトラップは既存踏襲で集約前に除外しておく。
            const cartLines = cartItems
              .filter(function (item) {
                return !(item.properties && item.properties[CART_PROPS.giftWrap]);
              })
              .map(function (item) {
                return {
                  productId: item.product_id,
                  variantId: item.variant_id,
                  isPreorder: !!(item.properties && item.properties[CART_PROPS.preOrder]),
                };
              });
            const cartHasNormal = cartLines.some(function (l) {
              return !l.isPreorder;
            });
            const preorderLines = cartLines.filter(function (l) {
              return l.isPreorder;
            });
            const cartHasPreorder = preorderLines.length > 0;

            if (isPreorderLine && cartHasNormal) {
              AlphaErrorModal.show({ title: ALERT_MESSAGES.preOrderBlockedByNormal });
              return;
            }
            if (!isPreorderLine && cartHasPreorder) {
              AlphaErrorModal.show({ title: ALERT_MESSAGES.normalBlockedByPreOrder });
              return;
            }

            // 予約同士の制限（厳格仕様: 予約はカート内に高々 1 点まで）
            // 同一 variant の数量追加・同一品番の別variant・別品番、いずれも無条件ブロック
            if (isPreorderLine && cartHasPreorder) {
              AlphaErrorModal.show({ title: ALERT_MESSAGES.preOrderAlreadyInCart });
              return;
            }

            mainForm.dataset.preorderValidateDone = '1';
            if (typeof mainForm.requestSubmit === 'function') {
              mainForm.requestSubmit();
            } else {
              mainForm.submit();
            }
          })
          .catch(function (err) {
            console.error('Preorder cart validation error:', err);
            AlphaErrorModal.show({ title: ALERT_MESSAGES.cartValidationFailed });
          });
      },
      true,
    );
  });
})();
